/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Events overview", description = "Hightouch Events collects customer behavior from websites, mobile apps, and backend systems, then stores it in your warehouse for analytics, activation, and real-time workflows.", lastUpdatedDate = new Date(1791763200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Events overview",
        description: "Hightouch Events collects customer behavior from websites, mobile apps, and backend systems, then stores it in your warehouse for analytics, activation, and real-time workflows.",
        lastUpdatedDate: "Mon Oct 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "What an event is",
          "depth": 0,
          "id": "what-an-event-is"
        }, {
          "value": "How an event moves through Hightouch",
          "depth": 0,
          "id": "how-an-event-moves-through-hightouch"
        }, {
          "value": "Your warehouse is the source of truth",
          "depth": 0,
          "id": "your-warehouse-is-the-source-of-truth"
        }, {
          "value": "Core features",
          "depth": 0,
          "id": "core-features"
        }, {
          "value": "Collect events from your apps and systems",
          "depth": 1,
          "id": "collect-events-from-your-apps-and-systems"
        }, {
          "value": "Send events to your warehouse and tools",
          "depth": 1,
          "id": "send-events-to-your-warehouse-and-tools"
        }, {
          "value": "Route events between sources and destinations",
          "depth": 1,
          "id": "route-events-between-sources-and-destinations"
        }, {
          "value": "Enforce schemas and transform events",
          "depth": 1,
          "id": "enforce-schemas-and-transform-events"
        }, {
          "value": "Get started",
          "depth": 0,
          "id": "get-started"
        }, {
          "value": "When to use Events",
          "depth": 0,
          "id": "when-to-use-events"
        }, {
          "value": "Use Events with other Hightouch products",
          "depth": 0,
          "id": "use-events-with-other-hightouch-products"
        }, {
          "value": "Permissions",
          "depth": 0,
          "id": "permissions"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      img: "img",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      code: "code",
      a: "a",
      ol: "ol",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Audience"
            }), _jsx(_components.th, {
              children: "How you'll use this article"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Marketing"
              })
            }), _jsx(_components.td, {
              children: "Understand what event data is used for and how it supports activation, personalization, and experimentation."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Data teams"
              })
            }), _jsx(_components.td, {
              children: "Learn how events are collected, structured, stored, and reused across analytics and activation workflows."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Engineering"
              })
            }), _jsx(_components.td, {
              children: "Understand the event model, ingestion paths, and how Events connects to your data architecture."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Hightouch Events collects customer behavior from websites, mobile apps, and backend systems, then stores that data in your warehouse. Teams can use the same event data to analyze behavior, build audiences, trigger journeys, and power real-time workflows."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/events-architecture.png",
          alt: "Events architecture"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch Events lets you collect customer behavior from your website, app, or backend systems and store it in your warehouse. Once events are flowing, you can use that data to build audiences, trigger journeys, measure campaigns, and power real-time personalization."
      }), "\n", _jsx(_components.p, {
        children: "This page explains what an event is, how Events works, and how it connects to your other tools."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "what-an-event-is",
        children: "What an event is"
      }), "\n", _jsx(_components.p, {
        children: "An event is a record of a single customer action, such as viewing a product, completing a purchase, or signing up. Every event carries three things:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Identity"
          }), " — who acted, as an ", _jsx(_components.code, {
            children: "anonymousId"
          }), " (a device or browser, tracked before someone is known) and, once known, a ", _jsx(_components.code, {
            children: "userId"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Properties or traits"
          }), " — the details. Properties describe the action (a cart total). Traits describe the person (an email, a plan)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Context"
          }), " — metadata the SDK collects automatically, such as device, page URL, or campaign parameters."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Events come in five types — ", _jsx(_components.code, {
          children: "identify"
        }), ", ", _jsx(_components.code, {
          children: "track"
        }), ", ", _jsx(_components.code, {
          children: "page"
        }), ", ", _jsx(_components.code, {
          children: "screen"
        }), ", and ", _jsx(_components.code, {
          children: "group"
        }), " — each for a different kind of action. See ", _jsx(_components.a, {
          href: "/events/event-spec",
          children: "event types and payload structure"
        }), " for the full field reference."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-an-event-moves-through-hightouch",
        children: "How an event moves through Hightouch"
      }), "\n", _jsx(_components.p, {
        children: "An event moves through three stages. Knowing where each happens tells you what you own and what Hightouch handles."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "You send the event."
          }), " You instrument your app, site, or backend with a Hightouch SDK, the HTTP API, or a streaming source like Kafka or Google Pub/Sub. This is your code, and your data quality starts here."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Hightouch validates and structures it."
          }), " Hightouch checks the event against any ", _jsx(_components.a, {
            href: "/events/contracts/management",
            children: "data contract"
          }), " you've defined, optionally transforms it with a ", _jsx(_components.a, {
            href: "/events/functions/overview",
            children: "function"
          }), ", and puts it into a consistent shape."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Hightouch delivers it."
          }), " Hightouch writes the event to ", _jsx(_components.a, {
            href: "/events/warehouses/warehouse",
            children: "your warehouse"
          }), ", streams it to ", _jsx(_components.a, {
            href: "/events/event-streaming",
            children: "downstream tools"
          }), ", or both."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You own the instrumentation and the warehouse. Hightouch owns receiving, validating, structuring, and delivering."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "your-warehouse-is-the-source-of-truth",
        children: "Your warehouse is the source of truth"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch writes events into your own warehouse as tables — ", _jsx(_components.code, {
          children: "identifies"
        }), ", ", _jsx(_components.code, {
          children: "tracks"
        }), ", ", _jsx(_components.code, {
          children: "pages"
        }), ", ", _jsx(_components.code, {
          children: "screens"
        }), ", and ", _jsx(_components.code, {
          children: "groups"
        }), " — and new fields you send become new columns automatically. Events doesn't keep a separate copy of your customer profiles. Your warehouse holds the event data and is the source of truth; other Hightouch products read from it rather than from a separate store."]
      }), "\n", _jsxs(_components.p, {
        children: ["Because your tables mirror the data you send, consistent event names, property names, and types matter — the warehouse reflects your input rather than cleaning it. See ", _jsx(_components.a, {
          href: "/events/warehouses/schema",
          children: "Warehouse schema"
        }), " for the table reference."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're coming from a tool that stored and served customer profiles for you, this is the largest shift to plan for. The logic that lived in that tool moves to your warehouse: Identity Resolution unifies a person's identifiers into one resolved identity, and ", _jsx(_components.a, {
          href: "/customer-studio/overview",
          children: "Customer Studio"
        }), " builds traits and audiences on top."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "core-features",
        children: "Core features"
      }), "\n", _jsx(_components.h3, {
        id: "collect-events-from-your-apps-and-systems",
        children: "Collect events from your apps and systems"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/events/sources/overview",
          children: "Event sources"
        }), " are where events come from. Hightouch provides SDKs for ", _jsx(_components.a, {
          href: "/events/sdks/browser",
          children: "browser"
        }), ", ", _jsx(_components.a, {
          href: "/events/sdks/ios",
          children: "iOS"
        }), ", ", _jsx(_components.a, {
          href: "/events/sdks/android",
          children: "Android"
        }), ", ", _jsx(_components.a, {
          href: "/events/sdks/nodejs",
          children: "Node.js"
        }), ", and other platforms, plus an ", _jsx(_components.a, {
          href: "/events/sdks/http",
          children: "HTTP API"
        }), " for server-side ingestion and connectors for streaming platforms like ", _jsx(_components.a, {
          href: "/events/sources/kafka",
          children: "Kafka"
        }), ", ", _jsx(_components.a, {
          href: "/events/sources/google-pubsub",
          children: "Google Pub/Sub"
        }), ", and ", _jsx(_components.a, {
          href: "/events/sources/amazon-sqs",
          children: "Amazon SQS"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "send-events-to-your-warehouse-and-tools",
        children: "Send events to your warehouse and tools"
      }), "\n", _jsxs(_components.p, {
        children: ["Event destinations are where events go. You can ", _jsx(_components.a, {
          href: "/events/warehouses/warehouse",
          children: "write events to your warehouse"
        }), " for analytics and modeling, or ", _jsx(_components.a, {
          href: "/events/event-streaming",
          children: "stream events to downstream tools"
        }), " like your email platform, CRM, or analytics service."]
      }), "\n", _jsx(_components.h3, {
        id: "route-events-between-sources-and-destinations",
        children: "Route events between sources and destinations"
      }), "\n", _jsx(_components.p, {
        children: "Event syncs define how events move between sources and destinations — which events are sent, how they're filtered, and how data is mapped."
      }), "\n", _jsx(_components.h3, {
        id: "enforce-schemas-and-transform-events",
        children: "Enforce schemas and transform events"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/events/contracts/management",
          children: "data contracts"
        }), " to enforce event schemas and catch violations before bad data reaches your warehouse. The ", _jsx(_components.a, {
          href: "/events/contracts/cli",
          children: "Events CLI"
        }), " generates typed SDK wrappers from those contracts so instrumentation is checked in your editor. Use ", _jsx(_components.a, {
          href: "/events/functions/overview",
          children: "functions"
        }), " to update events before they reach a destination — for example, rename properties, filter events, or add fields to the event data."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "get-started",
        children: "Get started"
      }), "\n", _jsxs(_components.p, {
        children: ["Events is usually set up by engineering and data teams. Start with ", _jsx(_components.a, {
          href: "/events/implementation-plan",
          children: "Plan your implementation"
        }), " to make the decisions every setup depends on — your event model, identity, contracts, coverage, and delivery. Then follow the path that matches your situation:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Migrating"
          }), " from another event collection tool, such as Segment or RudderStack? Most teams adopting Events are. The ", _jsx(_components.a, {
            href: "/events/migration-guide/overview",
            children: "Migration guide"
          }), " keeps your existing data flowing while you switch: you run both tools in parallel, confirm the new data matches, and cut over downstream tools deliberately."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Implementing fresh"
          }), ", with no existing event collection? Follow the ", _jsx(_components.a, {
            href: "/events/implementation-plan#build-your-implementation",
            children: "build steps in Plan"
          }), " — connect your sources, send events to your warehouse, define contracts, validate, and monitor — with no parallel run or cutover to manage."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "when-to-use-events",
        children: "When to use Events"
      }), "\n", _jsx(_components.p, {
        children: "Use Events when you need to collect behavioral data from your app, website, or backend systems and store it in your warehouse. For activating warehouse data that's already been collected and modeled, use Reverse ETL or Customer Studio instead."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Scenario"
            }), _jsx(_components.th, {
              children: "Recommended approach"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Collect behavioral data from websites, apps, or backend systems"
            }), _jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Write event data to your warehouse for analytics and modeling"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Events"
              }), " ", _jsx(_components.a, {
                href: "/events/warehouses/warehouse",
                children: "warehouse destinations"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Stream events to downstream tools in real time"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Events"
              }), " ", _jsx(_components.a, {
                href: "/events/event-streaming",
                children: "event streaming"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Enforce event schemas and catch data quality issues"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Events"
              }), " ", _jsx(_components.a, {
                href: "/events/contracts/management",
                children: "data contracts"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Build audiences using behavioral event data"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/customer-studio/overview",
                children: "Customer Studio"
              }), " — uses Events data as audience filters"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Respond to user behavior during an active session"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/real-time/overview",
                children: "Real-Time"
              }), " — evaluates audience membership using live events"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Sync modeled warehouse data to business tools"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/getting-started/concepts",
                children: "Reverse ETL"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-events-with-other-hightouch-products",
        children: "Use Events with other Hightouch products"
      }), "\n", _jsx(_components.p, {
        children: "Events captures what customers do on your site, app, and backend, and writes it to your warehouse. From there, your behavioral data moves through the rest of the Hightouch pipeline, with your warehouse at the center:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Collect"
          }), " — Events captures behavior from your app, website, and backend through SDKs, the ", _jsx(_components.a, {
            href: "/events/sdks/http",
            children: "HTTP API"
          }), ", and streaming sources like ", _jsx(_components.a, {
            href: "/events/sources/kafka",
            children: "Kafka"
          }), ", ", _jsx(_components.a, {
            href: "/events/sources/google-pubsub",
            children: "Google Pub/Sub"
          }), ", and ", _jsx(_components.a, {
            href: "/events/sources/amazon-sqs",
            children: "Amazon SQS"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Store"
          }), " — Events writes that behavior to ", _jsx(_components.a, {
            href: "/events/warehouses/warehouse",
            children: "your warehouse"
          }), " in a consistent schema. The warehouse is the source of truth for every stage that follows."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Unify"
          }), " — Identity Resolution links a person's identifiers across devices, sessions, and sources into one resolved identity."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Model"
          }), " — ", _jsx(_components.a, {
            href: "/customer-studio/overview",
            children: "Customer Studio"
          }), " builds traits, audiences, and journeys on that data, without SQL."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Activate"
          }), " — send the result to your tools with ", _jsx(_components.a, {
            href: "/getting-started/concepts",
            children: "Reverse ETL"
          }), ", evaluate it live with ", _jsx(_components.a, {
            href: "/real-time/overview",
            children: "Real-Time Personalization"
          }), ", or optimize per person with ", _jsx(_components.a, {
            href: "/ai-decisioning/overview",
            children: "AI Decisioning"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You don't need every stage. Events plus your warehouse already gives you queryable behavioral data for analytics; identity unification, modeling, and activation layer on as you need them. For time-sensitive cases, Events can also ", _jsx(_components.a, {
          href: "/events/event-streaming",
          children: "stream events"
        }), " straight to a destination, bypassing the warehouse round trip."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["Access to Events resources is controlled through custom roles. Admins can configure Events-specific grants in the ", _jsx(_components.strong, {
          children: "Events"
        }), " tab of the custom role builder, including workspace-level creation grants (for creating new event sources, destinations, contracts, and functions) and per-resource grants (for managing individual resources)."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Events permissions require Permissions V2. To create or manage an event sync,\nusers need permissions on ", _jsx(_components.strong, {
            children: "both"
          }), " the event source and the event destination."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/workspace-management/roles#events",
          children: "Roles"
        }), " for the full list of Events grants."]
      })]
    });
  }
}
export default MDXContent;
